import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  UserCircle, ShieldCheck, ClockCountdown, Eye, Plus, Trash, X,
  Camera, Warning, CheckCircle, UserFocus, Buildings, Siren,
  ToggleLeft, ToggleRight, PencilSimple, MagnifyingGlass,
  CalendarBlank, ArrowRight, FunnelSimple, UserList, Broadcast
} from '@phosphor-icons/react';

const ROLES = ['family', 'staff', 'visitor'];
const ACTIONS = ['alert', 'lock_down', 'alarm'];

export default function FaceRecognitionPage() {
  const { api, user } = useAuth();
  const [tab, setTab] = useState('profiles');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/face-recognition/stats');
      setStats(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const tabs = [
    { id: 'profiles', label: 'Face Profiles', icon: UserFocus },
    { id: 'zones', label: 'Security Zones', icon: Buildings },
    { id: 'attendance', label: 'Staff Attendance', icon: ClockCountdown },
    { id: 'detections', label: 'Detection Log', icon: Eye },
    { id: 'sensors', label: 'Motion Sensors', icon: Broadcast },
  ];

  return (
    <div data-testid="face-recognition-page" className="space-y-4 sm:space-y-6 overflow-x-hidden max-w-full">
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0">
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">AI Surveillance</p>
          <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight mt-1">Face Recognition</h1>
        </div>
      </div>

      {/* Stats Strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="face-stats">
          <StatCard label="Known Faces" value={stats.total_profiles} color="#00E5FF" />
          <StatCard label="Active Zones" value={stats.active_zones} color="#22C55E" />
          <StatCard label="Violations Today" value={stats.violations_today} color="#EF4444" />
          <StatCard label="Unknown Today" value={stats.unknown_today} color="#F59E0B" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto max-w-full" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 ${tab === t.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'profiles' && <FaceProfilesTab api={api} user={user} onUpdate={fetchStats} />}
      {tab === 'zones' && <SecurityZonesTab api={api} user={user} onUpdate={fetchStats} />}
      {tab === 'attendance' && <AttendanceTab api={api} />}
      {tab === 'detections' && <DetectionLogTab api={api} />}
      {tab === 'sensors' && <MotionSensorsTab api={api} user={user} onUpdate={fetchStats} />}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</p>
      <p className="text-lg sm:text-2xl font-heading font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

// ========== FACE PROFILES TAB ==========
function FaceProfilesTab({ api, user, onUpdate }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'family', photo: '', notes: '', allowed_areas: [], allowed_hours: '' });
  const [rooms, setRooms] = useState([]);

  const fetch = useCallback(async () => {
    try {
      const [pRes, rRes] = await Promise.all([
        api.get('/face-recognition/profiles'),
        api.get('/control-center/rooms'),
      ]);
      setProfiles(pRes.data);
      setRooms(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name.trim()) return;
    try {
      if (editId) {
        await api.put(`/face-recognition/profiles/${editId}`, form);
      } else {
        await api.post('/face-recognition/profiles', form);
      }
      setShowForm(false); setEditId(null);
      setForm({ name: '', role: 'family', photo: '', notes: '', allowed_areas: [], allowed_hours: '' });
      fetch(); onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this face profile?')) return;
    try { await api.delete(`/face-recognition/profiles/${id}`); fetch(); onUpdate(); } catch (e) { console.error(e); }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-400">{profiles.length} registered face(s)</p>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', role: 'family', photo: '', notes: '', allowed_areas: [], allowed_hours: '' }); }}
          data-testid="add-profile-btn"
          className="px-3 sm:px-4 py-2 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 btn-glow flex-shrink-0">
          <Plus size={14} /> Add Face
        </button>
      </div>

      {profiles.length === 0 ? (
        <EmptyState icon={UserFocus} text="No face profiles registered. Add family, staff, or visitor faces." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {profiles.map(p => (
            <div key={p.profile_id} data-testid={`profile-${p.profile_id}`}
              className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden"
                  style={{ background: p.photo ? 'transparent' : 'rgba(255,107,53,0.1)' }}>
                  {p.photo ? (
                    <img src={p.photo} alt={p.name} className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <UserCircle size={28} className="text-[#FF6B35]" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      p.role === 'family' ? 'bg-blue-500/10 text-blue-400' :
                      p.role === 'staff' ? 'bg-green-500/10 text-green-400' :
                      'bg-yellow-500/10 text-yellow-400'
                    }`}>{p.role}</span>
                    {p.detection_count > 0 && <span className="text-[10px] text-neutral-500">Seen {p.detection_count}x</span>}
                  </div>
                  {p.last_seen && (
                    <p className="text-[10px] text-neutral-500 mt-1">Last: {new Date(p.last_seen).toLocaleString()}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => { setEditId(p.profile_id); setForm({ name: p.name, role: p.role, photo: p.photo || '', notes: p.notes || '', allowed_areas: p.allowed_areas || [], allowed_hours: p.allowed_hours || '' }); setShowForm(true); }}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500"><PencilSimple size={14} /></button>
                  <button onClick={() => handleDelete(p.profile_id)}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Profile Form Modal */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditId(null); }}>
          <h3 className="text-lg font-bold mb-4">{editId ? 'Edit Face Profile' : 'Register New Face'}</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ background: form.photo ? 'transparent' : 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                {form.photo ? (
                  <img src={form.photo} alt="preview" className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Camera size={24} className="text-neutral-500" />
                )}
              </div>
              <label className="px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                Upload Photo
                <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} data-testid="photo-upload" />
              </label>
            </div>
            <InputField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testId="profile-name" placeholder="e.g., Ramesh" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Role</label>
              <div className="flex gap-2">
                {ROLES.map(r => (
                  <button key={r} onClick={() => setForm(f => ({ ...f, role: r }))}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${form.role === r ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'bg-white/5 text-neutral-400'}`}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Allowed Areas (optional)</label>
              <div className="flex flex-wrap gap-2">
                {rooms.map(r => (
                  <button key={r.room_id || r.name} onClick={() => {
                    setForm(f => ({
                      ...f,
                      allowed_areas: f.allowed_areas.includes(r.name)
                        ? f.allowed_areas.filter(a => a !== r.name)
                        : [...f.allowed_areas, r.name]
                    }));
                  }}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${form.allowed_areas.includes(r.name) ? 'bg-[#FF6B35]/15 text-[#FF6B35] ring-1 ring-[#FF6B35]/30' : 'bg-white/5 text-neutral-400'}`}>
                    {r.name}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-neutral-600 mt-1">Empty = allowed everywhere</p>
            </div>
            <InputField label="Allowed Hours (optional)" value={form.allowed_hours} onChange={v => setForm(f => ({ ...f, allowed_hours: v }))} placeholder="e.g., 06:00-22:00" />
            <InputField label="Notes" value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Optional notes" />
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="flex-1 py-3 rounded-xl text-sm font-semibold text-neutral-400" style={{ background: 'rgba(255,255,255,0.05)' }}>Cancel</button>
            <button onClick={handleSave} data-testid="save-profile-btn" className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">{editId ? 'Update' : 'Register'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========== SECURITY ZONES TAB ==========
function SecurityZonesTab({ api, user, onUpdate }) {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({ name: '', area: '', allowed_roles: ['family'], allowed_profiles: [], restricted_hours: '', action_on_violation: 'alert', enabled: true });

  const fetch = useCallback(async () => {
    try {
      const [zRes, rRes] = await Promise.all([
        api.get('/face-recognition/zones'),
        api.get('/control-center/rooms'),
      ]);
      setZones(zRes.data);
      setRooms(rRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.area) return;
    try {
      if (editId) {
        await api.put(`/face-recognition/zones/${editId}`, form);
      } else {
        await api.post('/face-recognition/zones', form);
      }
      setShowForm(false); setEditId(null);
      setForm({ name: '', area: '', allowed_roles: ['family'], allowed_profiles: [], restricted_hours: '', action_on_violation: 'alert', enabled: true });
      fetch(); onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this security zone?')) return;
    try { await api.delete(`/face-recognition/zones/${id}`); fetch(); onUpdate(); } catch (e) { console.error(e); }
  };

  const handleToggle = async (id) => {
    try { await api.put(`/face-recognition/zones/${id}/toggle`); fetch(); } catch (e) { console.error(e); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-400">{zones.length} security zone(s)</p>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', area: rooms[0]?.name || '', allowed_roles: ['family'], allowed_profiles: [], restricted_hours: '', action_on_violation: 'alert', enabled: true }); }}
          data-testid="add-zone-btn"
          className="px-3 sm:px-4 py-2 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 btn-glow flex-shrink-0">
          <Plus size={14} /> Add Zone
        </button>
      </div>

      {zones.length === 0 ? (
        <EmptyState icon={Buildings} text="No security zones defined. Create restricted areas with time-based rules." />
      ) : (
        <div className="space-y-3" data-testid="zones-list">
          {zones.map(z => (
            <div key={z.zone_id} data-testid={`zone-${z.zone_id}`}
              className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', opacity: z.enabled ? 1 : 0.5 }}>
              <div className="flex items-start sm:items-center justify-between gap-2">
                <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: z.action_on_violation === 'alarm' ? 'rgba(239,68,68,0.1)' : z.action_on_violation === 'lock_down' ? 'rgba(245,158,11,0.1)' : 'rgba(0,229,255,0.1)' }}>
                    {z.action_on_violation === 'alarm' ? <Siren size={18} className="text-red-400" /> :
                     z.action_on_violation === 'lock_down' ? <ShieldCheck size={18} className="text-yellow-400" /> :
                     <Warning size={18} className="text-[#00E5FF]" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate">{z.name}</p>
                    <div className="flex items-center gap-1.5 text-[10px] text-neutral-500 flex-wrap">
                      <span className="uppercase">{z.area}</span>
                      <span>·</span>
                      <span>Allowed: {z.allowed_roles.join(', ')}</span>
                      {z.restricted_hours && <><span>·</span><span>{z.restricted_hours}</span></>}
                      {z.violation_count > 0 && <><span>·</span><span className="text-red-400">{z.violation_count} violations</span></>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleToggle(z.zone_id)} className="p-1.5 rounded-xl hover:bg-white/5">
                    {z.enabled ? <ToggleRight size={20} weight="fill" className="text-green-400" /> : <ToggleLeft size={20} className="text-neutral-500" />}
                  </button>
                  <button onClick={() => { setEditId(z.zone_id); setForm({ name: z.name, area: z.area, allowed_roles: z.allowed_roles, allowed_profiles: z.allowed_profiles || [], restricted_hours: z.restricted_hours, action_on_violation: z.action_on_violation, enabled: z.enabled }); setShowForm(true); }}
                    className="p-1.5 rounded-xl hover:bg-white/5 text-neutral-500"><PencilSimple size={14} /></button>
                  <button onClick={() => handleDelete(z.zone_id)} className="p-1.5 rounded-xl hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Zone Form Modal */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditId(null); }}>
          <h3 className="text-lg font-bold mb-4">{editId ? 'Edit Security Zone' : 'New Security Zone'}</h3>
          <div className="space-y-3">
            <InputField label="Zone Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testId="zone-name" placeholder="e.g., Master Bedroom Night Guard" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Area / Room</label>
              <select value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} data-testid="zone-area"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                <option value="">Select room</option>
                {rooms.map(r => <option key={r.room_id || r.name} value={r.name}>{r.name}</option>)}
                <option value="Entrance">Entrance</option>
                <option value="Backyard">Backyard</option>
                <option value="Parking">Parking</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Allowed Roles</label>
              <div className="flex gap-2">
                {ROLES.map(r => (
                  <button key={r} onClick={() => {
                    setForm(f => ({
                      ...f,
                      allowed_roles: f.allowed_roles.includes(r)
                        ? f.allowed_roles.filter(x => x !== r)
                        : [...f.allowed_roles, r]
                    }));
                  }}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${form.allowed_roles.includes(r) ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'bg-white/5 text-neutral-400'}`}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <InputField label="Restricted Hours" value={form.restricted_hours} onChange={v => setForm(f => ({ ...f, restricted_hours: v }))} placeholder="e.g., 22:00-06:00 (leave empty for 24/7)" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Action on Violation</label>
              <div className="flex gap-2">
                {ACTIONS.map(a => (
                  <button key={a} onClick={() => setForm(f => ({ ...f, action_on_violation: a }))}
                    className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${form.action_on_violation === a ? 'ring-2 ring-[#FF6B35] bg-[#FF6B35]/10 text-[#FF6B35]' : 'bg-white/5 text-neutral-400'}`}>
                    {a === 'lock_down' ? 'Lock Down' : a.charAt(0).toUpperCase() + a.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="flex-1 py-3 rounded-xl text-sm font-semibold text-neutral-400" style={{ background: 'rgba(255,255,255,0.05)' }}>Cancel</button>
            <button onClick={handleSave} data-testid="save-zone-btn" className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">{editId ? 'Update' : 'Create Zone'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========== ATTENDANCE TAB ==========
function AttendanceTab({ api }) {
  const [summary, setSummary] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedStaff, setSelectedStaff] = useState('');

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, recRes] = await Promise.all([
        api.get(`/face-recognition/attendance/summary?month=${month}`),
        api.get(`/face-recognition/attendance?month=${month}${selectedStaff ? `&name=${selectedStaff}` : ''}`),
      ]);
      setSummary(sumRes.data);
      setRecords(recRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, month, selectedStaff]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <FunnelSimple size={16} className="text-neutral-500" />
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
        {summary.length > 0 && (
          <select value={selectedStaff} onChange={e => setSelectedStaff(e.target.value)}
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
            <option value="">All Staff</option>
            {summary.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        )}
      </div>

      {/* Summary Cards */}
      {summary.length > 0 && !selectedStaff && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="attendance-summary">
          {summary.map(s => (
            <div key={s.name} className="rounded-2xl p-4 cursor-pointer hover:ring-1 hover:ring-[#FF6B35]/30 transition-all"
              onClick={() => setSelectedStaff(s.name)}
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold">{s.name}</p>
                <ArrowRight size={14} className="text-neutral-500" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[10px] text-neutral-500 uppercase">Days</p>
                  <p className="text-lg font-bold text-green-400">{s.days_present}</p>
                </div>
                <div>
                  <p className="text-[10px] text-neutral-500 uppercase">Hours</p>
                  <p className="text-lg font-bold text-[#00E5FF]">{s.total_hours}</p>
                </div>
                <div>
                  <p className="text-[10px] text-neutral-500 uppercase">Late</p>
                  <p className={`text-lg font-bold ${s.late_arrivals > 0 ? 'text-red-400' : 'text-neutral-600'}`}>{s.late_arrivals}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Records */}
      {(selectedStaff || summary.length === 0) && (
        <div className="space-y-2">
          {selectedStaff && (
            <button onClick={() => setSelectedStaff('')} className="text-xs text-[#FF6B35] font-semibold mb-2 flex items-center gap-1">
              <X size={12} /> Clear filter
            </button>
          )}
          {records.length === 0 ? (
            <EmptyState icon={ClockCountdown} text="No attendance records for this period." />
          ) : (
            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {/* Desktop */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider border-b border-white/5">
                      <th className="p-3">Date</th>
                      <th className="p-3">Name</th>
                      <th className="p-3">Check In</th>
                      <th className="p-3">Check Out</th>
                      <th className="p-3">Hours</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map(r => (
                      <tr key={r.attendance_id} className="border-b border-white/5">
                        <td className="p-3 text-neutral-400">{r.date}</td>
                        <td className="p-3 font-medium">{r.name}</td>
                        <td className="p-3 text-neutral-400">{r.check_in ? new Date(r.check_in).toLocaleTimeString() : '-'}</td>
                        <td className="p-3 text-neutral-400">{r.check_out ? new Date(r.check_out).toLocaleTimeString() : '-'}</td>
                        <td className="p-3 text-[#00E5FF] font-semibold">{r.total_hours ? `${r.total_hours}h` : '-'}</td>
                        <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-500/10 text-green-400">Present</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile */}
              <div className="sm:hidden divide-y divide-white/5">
                {records.map(r => (
                  <div key={r.attendance_id} className="p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={14} className="text-green-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium truncate">{r.name}</p>
                        <p className="text-sm font-bold text-[#00E5FF] flex-shrink-0">{r.total_hours ? `${r.total_hours}h` : '-'}</p>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5">
                        <span>{r.date}</span>
                        <span>|</span>
                        <span>In: {r.check_in ? new Date(r.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                        {r.check_out && <><span>|</span><span>Out: {new Date(r.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ========== DETECTION LOG TAB ==========
function DetectionLogTab({ api }) {
  const [detections, setDetections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [cameras, setCameras] = useState([]);
  const [analyzing, setAnalyzing] = useState('');
  const [scanStatus, setScanStatus] = useState(null);
  const [scanningAll, setScanningAll] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const [dRes, cRes, sRes] = await Promise.all([
        api.get(`/face-recognition/detections?limit=50${filter ? `&threat_level=${filter}` : ''}`),
        api.get('/cameras'),
        api.get('/face-recognition/auto-scan-status'),
      ]);
      setDetections(dRes.data);
      setCameras(cRes.data);
      setScanStatus(sRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, filter]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAnalyze = async (cameraId) => {
    setAnalyzing(cameraId);
    try {
      await api.post(`/face-recognition/analyze/${cameraId}`);
      fetch();
    } catch (e) { console.error(e); }
    setAnalyzing('');
  };

  const handleScanAll = async () => {
    setScanningAll(true);
    try {
      await api.post('/face-recognition/scan-all');
      fetch();
    } catch (e) { console.error(e); }
    setScanningAll(false);
  };

  const handleReview = async (detectionId) => {
    try {
      await api.put(`/face-recognition/detections/${detectionId}/review`);
      fetch();
    } catch (e) { console.error(e); }
  };

  if (loading) return <Spinner />;

  const threatColors = { clear: 'text-green-400 bg-green-500/10', low: 'text-blue-400 bg-blue-500/10', medium: 'text-yellow-400 bg-yellow-500/10', high: 'text-red-400 bg-red-500/10', critical: 'text-red-500 bg-red-500/20' };

  return (
    <div className="space-y-4">
      {/* Auto-Scan Status Banner */}
      {scanStatus && (
        <div className="rounded-2xl p-3 sm:p-4" data-testid="auto-scan-status"
          style={{ background: scanStatus.enabled ? 'rgba(34,197,94,0.05)' : 'rgba(255,255,255,0.02)', border: `1px solid ${scanStatus.enabled ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)'}` }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${scanStatus.enabled ? 'bg-green-400 animate-pulse' : 'bg-neutral-600'}`} />
              <p className="text-xs font-semibold">
                {scanStatus.enabled
                  ? `Auto-Scan Active`
                  : 'Auto-Scan Disabled'}
              </p>
              {scanStatus.enabled && (
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  scanStatus.scan_mode === 'motion' ? 'bg-blue-500/10 text-blue-400' :
                  scanStatus.scan_mode === 'both' ? 'bg-purple-500/10 text-purple-400' :
                  'bg-green-500/10 text-green-400'
                }`}>
                  {scanStatus.scan_mode === 'motion' ? 'Motion' :
                   scanStatus.scan_mode === 'both' ? 'Interval + Motion' :
                   `Every ${scanStatus.interval_minutes} min`}
                </span>
              )}
              {scanStatus.enabled && scanStatus.last_run && (
                <span className="text-[10px] text-neutral-500">
                  Last scan: {new Date(scanStatus.last_run).toLocaleTimeString()}
                </span>
              )}
              {scanStatus.enabled && scanStatus.last_motion_event && scanStatus.scan_mode !== 'interval' && (
                <span className="text-[10px] text-blue-400">
                  Last motion: {scanStatus.last_motion_camera} at {new Date(scanStatus.last_motion_event).toLocaleTimeString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-neutral-500">{scanStatus.cameras_total} camera(s)</span>
              {scanStatus.scan_mode !== 'interval' && scanStatus.motion_sensors_active > 0 && (
                <span className="text-[10px] text-blue-400">{scanStatus.motion_sensors_active} sensor(s)</span>
              )}
              {!scanStatus.enabled && (
                <span className="text-[10px] text-neutral-500">Enable in Settings &gt; Devices</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scan Cameras */}
      {cameras.length > 0 && (
        <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,107,53,0.05)', border: '1px solid rgba(255,107,53,0.15)' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-[#FF6B35]">Manual Camera Scan</p>
            <button onClick={handleScanAll} disabled={scanningAll} data-testid="scan-all-btn"
              className="px-3 py-1.5 rounded-xl text-[11px] font-bold bg-[#FF6B35] text-white disabled:opacity-50">
              {scanningAll ? 'Scanning...' : 'Scan All Cameras'}
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {cameras.map(c => (
              <button key={c.camera_id} onClick={() => handleAnalyze(c.camera_id)}
                disabled={analyzing === c.camera_id}
                data-testid={`scan-camera-${c.camera_id}`}
                className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all disabled:opacity-50"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <Camera size={12} />
                {analyzing === c.camera_id ? 'Analyzing...' : c.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <FunnelSimple size={16} className="text-neutral-500" />
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
          <option value="">All Threats</option>
          <option value="clear">Clear</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
      </div>

      {detections.length === 0 ? (
        <EmptyState icon={Eye} text="No detections yet. Scan cameras to start face detection." />
      ) : (
        <div className="space-y-3" data-testid="detections-list">
          {detections.map(d => (
            <div key={d.detection_id} data-testid={`detection-${d.detection_id}`}
              className={`rounded-2xl p-3 sm:p-4 ${d.reviewed ? 'opacity-60' : ''}`}
              style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${d.violations?.length > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${threatColors[d.threat_level] || threatColors.clear}`}>
                  {d.threat_level === 'clear' ? <CheckCircle size={18} /> : <Warning size={18} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold">{d.camera_name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${threatColors[d.threat_level] || ''}`}>
                        {d.threat_level}
                      </span>
                    </div>
                    <p className="text-[10px] text-neutral-500">{new Date(d.timestamp).toLocaleString()}</p>
                  </div>
                  <p className="text-xs text-neutral-400 mt-1">{d.details || d.activity}</p>
                  <div className="flex items-center gap-3 mt-2 flex-wrap text-[10px]">
                    <span className="text-neutral-500">{d.persons_detected} person(s)</span>
                    {d.identified?.length > 0 && (
                      <span className="text-green-400">Known: {d.identified.map(p => p.name).join(', ')}</span>
                    )}
                    {d.unknown_persons > 0 && (
                      <span className="text-red-400 font-bold">{d.unknown_persons} unknown</span>
                    )}
                  </div>
                  {d.violations?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {d.violations.map((v, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[10px] text-red-400">
                          <Siren size={10} /> <span>{v.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {!d.reviewed && (
                  <button onClick={() => handleReview(d.detection_id)}
                    className="p-1.5 rounded-lg hover:bg-white/5 text-neutral-500 hover:text-green-400 flex-shrink-0" title="Mark reviewed">
                    <CheckCircle size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========== MOTION SENSORS TAB ==========
function MotionSensorsTab({ api, user, onUpdate }) {
  const [sensors, setSensors] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [motionEvents, setMotionEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', camera_id: '', ha_entity_id: '', area: '', cooldown_seconds: 60, enabled: true });

  const fetch = useCallback(async () => {
    try {
      const [sRes, cRes, eRes] = await Promise.all([
        api.get('/face-recognition/motion-sensors'),
        api.get('/cameras'),
        api.get('/face-recognition/motion-events?limit=10'),
      ]);
      setSensors(sRes.data);
      setCameras(cRes.data);
      setMotionEvents(eRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.camera_id) return;
    try {
      if (editId) {
        await api.put(`/face-recognition/motion-sensors/${editId}`, form);
      } else {
        await api.post('/face-recognition/motion-sensors', form);
      }
      setShowForm(false); setEditId(null);
      setForm({ name: '', camera_id: '', ha_entity_id: '', area: '', cooldown_seconds: 60, enabled: true });
      fetch(); onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this motion sensor?')) return;
    try { await api.delete(`/face-recognition/motion-sensors/${id}`); fetch(); onUpdate(); } catch (e) { console.error(e); }
  };

  const handleToggle = async (id) => {
    try { await api.put(`/face-recognition/motion-sensors/${id}/toggle`); fetch(); } catch (e) { console.error(e); }
  };

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(0,100,255,0.05)', border: '1px solid rgba(0,100,255,0.15)' }}>
        <p className="text-xs font-semibold text-blue-400 mb-1">How Motion-Based Scanning Works</p>
        <p className="text-[11px] text-neutral-400">Link motion sensors to cameras. When motion is detected (via Home Assistant or webhook), the linked camera scans for faces immediately. Much more efficient than interval scanning — only uses API credits when there's actual activity.</p>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-400">{sensors.length} motion sensor(s)</p>
        <button onClick={() => { setShowForm(true); setEditId(null); setForm({ name: '', camera_id: cameras[0]?.camera_id || '', ha_entity_id: '', area: '', cooldown_seconds: 60, enabled: true }); }}
          data-testid="add-sensor-btn"
          className="px-3 sm:px-4 py-2 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 btn-glow flex-shrink-0">
          <Plus size={14} /> Add Sensor
        </button>
      </div>

      {sensors.length === 0 ? (
        <EmptyState icon={Broadcast} text="No motion sensors configured. Link sensors to cameras to enable motion-triggered scanning." />
      ) : (
        <div className="space-y-3" data-testid="sensors-list">
          {sensors.map(s => {
            const linkedCam = cameras.find(c => c.camera_id === s.camera_id);
            return (
              <div key={s.sensor_id} data-testid={`sensor-${s.sensor_id}`}
                className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', opacity: s.enabled ? 1 : 0.5 }}>
                <div className="flex items-start sm:items-center justify-between gap-2">
                  <div className="flex items-start sm:items-center gap-2.5 min-w-0 flex-1">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,100,255,0.1)' }}>
                      <Broadcast size={18} className="text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate">{s.name}</p>
                      <div className="flex items-center gap-1.5 text-[10px] text-neutral-500 flex-wrap">
                        <span>Camera: {linkedCam?.name || s.camera_id}</span>
                        {s.ha_entity_id && <><span>·</span><span>{s.ha_entity_id}</span></>}
                        <span>·</span>
                        <span>Cooldown: {s.cooldown_seconds}s</span>
                        {s.trigger_count > 0 && <><span>·</span><span>Triggered {s.trigger_count}x</span></>}
                        {s.last_triggered && <><span>·</span><span>Last: {new Date(s.last_triggered).toLocaleString()}</span></>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleToggle(s.sensor_id)} className="p-1.5 rounded-xl hover:bg-white/5">
                      {s.enabled ? <ToggleRight size={20} weight="fill" className="text-green-400" /> : <ToggleLeft size={20} className="text-neutral-500" />}
                    </button>
                    <button onClick={() => { setEditId(s.sensor_id); setForm({ name: s.name, camera_id: s.camera_id, ha_entity_id: s.ha_entity_id || '', area: s.area || '', cooldown_seconds: s.cooldown_seconds || 60, enabled: s.enabled }); setShowForm(true); }}
                      className="p-1.5 rounded-xl hover:bg-white/5 text-neutral-500"><PencilSimple size={14} /></button>
                    <button onClick={() => handleDelete(s.sensor_id)}
                      className="p-1.5 rounded-xl hover:bg-white/5 text-neutral-500 hover:text-red-400"><Trash size={14} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Webhook Info */}
      <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs font-semibold text-neutral-300 mb-2">Webhook Endpoint</p>
        <p className="text-[11px] text-neutral-500 mb-2">Configure Home Assistant or any external service to POST to this endpoint when motion is detected:</p>
        <code className="block bg-black/40 px-3 py-2 rounded-lg text-[11px] text-blue-400 break-all" data-testid="webhook-url">
          POST {window.location.origin}/api/face-recognition/motion-webhook
        </code>
        <p className="text-[11px] text-neutral-500 mt-2">Body options:</p>
        <div className="space-y-1 mt-1">
          <code className="block bg-black/40 px-3 py-1.5 rounded-lg text-[10px] text-neutral-400">{'{"sensor_id": "ms_xxxxx"}'}</code>
          <code className="block bg-black/40 px-3 py-1.5 rounded-lg text-[10px] text-neutral-400">{'{"ha_entity_id": "binary_sensor.front_door_motion"}'}</code>
          <code className="block bg-black/40 px-3 py-1.5 rounded-lg text-[10px] text-neutral-400">{'{"camera_id": "cam_xxxxx"}'}</code>
        </div>
      </div>

      {/* Recent Motion Events */}
      {motionEvents.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-neutral-400 mb-2">Recent Motion Events</p>
          <div className="space-y-2">
            {motionEvents.map(e => (
              <div key={e.event_id} className="flex items-center gap-3 p-2.5 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)' }}>
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                  <Broadcast size={12} className="text-blue-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium truncate">{e.camera_name || e.sensor_name || 'Unknown'}</p>
                    <p className="text-[10px] text-neutral-500 flex-shrink-0">{new Date(e.timestamp).toLocaleString()}</p>
                  </div>
                  <p className="text-[10px] text-neutral-500">{e.scan_triggered ? 'Scan triggered' : 'No scan'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sensor Form Modal */}
      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditId(null); }}>
          <h3 className="text-lg font-bold mb-4">{editId ? 'Edit Motion Sensor' : 'New Motion Sensor'}</h3>
          <div className="space-y-3">
            <InputField label="Sensor Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testId="sensor-name" placeholder="e.g., Front Door Motion" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Linked Camera</label>
              <select value={form.camera_id} onChange={e => setForm(f => ({ ...f, camera_id: e.target.value }))} data-testid="sensor-camera"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                <option value="">Select camera</option>
                {cameras.map(c => <option key={c.camera_id} value={c.camera_id}>{c.name}</option>)}
              </select>
            </div>
            <InputField label="Home Assistant Entity ID (optional)" value={form.ha_entity_id} onChange={v => setForm(f => ({ ...f, ha_entity_id: v }))} placeholder="binary_sensor.front_door_motion" />
            <InputField label="Area (optional)" value={form.area} onChange={v => setForm(f => ({ ...f, area: v }))} placeholder="e.g., Entrance" />
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">Cooldown (seconds)</label>
              <select value={form.cooldown_seconds} onChange={e => setForm(f => ({ ...f, cooldown_seconds: parseInt(e.target.value) }))} data-testid="sensor-cooldown"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none">
                <option value={30}>30 seconds</option>
                <option value={60}>60 seconds (default)</option>
                <option value={120}>2 minutes</option>
                <option value={300}>5 minutes</option>
              </select>
              <p className="text-[10px] text-neutral-600 mt-1">Min time between scans to prevent API overuse</p>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <button onClick={() => { setShowForm(false); setEditId(null); }} className="flex-1 py-3 rounded-xl text-sm font-semibold text-neutral-400" style={{ background: 'rgba(255,255,255,0.05)' }}>Cancel</button>
            <button onClick={handleSave} data-testid="save-sensor-btn" className="flex-1 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">{editId ? 'Update' : 'Add Sensor'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========== SHARED COMPONENTS ==========
function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }} onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl p-5 sm:p-6"
        style={{ background: 'rgba(30,30,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function InputField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        data-testid={testId} placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-12 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <Icon size={36} className="text-neutral-600 mx-auto mb-3" />
      <p className="text-sm text-neutral-500">{text}</p>
    </div>
  );
}
